// LoginForm.tsx
import { BusinessCenter, Logout, Person } from '@mui/icons-material';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { getUser, logoutUser } from 'lib/api';
import EnhancedBox from 'lib/designSystem/BigBox';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from 'services/redux/authSlice';
import { RootState } from 'services/redux/store';

const WaitingForAuthorization = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const auth = useSelector((state: RootState) => state.auth);
  const [userProfile, setUserProfile] = useState({
    name: '',
    email: '',
    encryptedId: '',
    company: { taxIdentifier: '', name: '' },
  });

  const fetchUserProfile = async () => {
    setLoading(true);
    const userProfile = await getUser(dispatch, { id: auth.user?.id });
    setUserProfile(userProfile);
    setLoading(false);
  };

  const handleLogout = async () => {
    const response = await logoutUser(dispatch);
    if (response) {
      dispatch(logout());
    }
  };

  useEffect(() => {
    void fetchUserProfile();
  }, []);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <Box sx={{ backgroundColor: '#F8FAFC', minHeight: '100vh', px: 3, py: 2 }}>
      <Box sx={{ margin: 'auto', maxWidth: '600px' }}>
        <EnhancedBox>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <Box
              sx={{
                backgroundColor: 'primary.main',
                borderRadius: '50%',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              <Person sx={{ fontSize: '1.5rem' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                }}
              >
                ¡Bienvenido {userProfile.name.split(' ')[0]}!
              </Typography>
              <Chip
                label="En espera de autorización"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: '20px', fontSize: '0.7rem' }}
              />
            </Box>
            <Tooltip title="Cerrar sesión">
              <IconButton
                onClick={() => void handleLogout()}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'error.main',
                    backgroundColor: 'rgba(211, 47, 47, 0.04)',
                  },
                }}
              >
                <Logout />
              </IconButton>
            </Tooltip>
          </Stack>
          <Divider sx={{ my: 1.5 }} />

          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontSize: '0.95rem',
              lineHeight: 1.5,
              mb: 3,
            }}
          >
            Espera a que tu empresa sea autorizada para acceder a Pruff Connect.
          </Typography>

          <Paper
            sx={{
              p: 2,
              boxShadow: 'none',
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              mb: 3,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  borderRadius: '50%',
                  p: 0.75,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <BusinessCenter
                  sx={{ fontSize: '1rem', color: 'primary.main' }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.75rem', fontWeight: 500 }}
                >
                  RUT Asociado a tu Cuenta
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    fontSize: '1.1rem',
                  }}
                >
                  {userProfile.company.taxIdentifier}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </EnhancedBox>
      </Box>
    </Box>
  );
};

export default WaitingForAuthorization;
